// paymentApi.js - API Service for Payment Operations

const BASE =
  process.env.REACT_APP_BACKEND_URL ||
  (typeof import.meta !== "undefined"
    ? import.meta.env?.VITE_BACKEND_URL
    : "") ||
  "http://localhost:5000"; 

/**
 * Update payment status for a subscription
 * @param {string} applicationNumber - The application number 
 * @param {string} paymentStatus - Either 'success' or 'failed'
 * @returns {Promise<Object>} Response data
 */
export const updatePaymentStatus = async (applicationNumber, paymentStatus) => {
  try {
    const response = await fetch(
      `${BASE}/payment-status/${applicationNumber}`, // Changed from applicationId
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          payment_status: paymentStatus // Make sure this matches backend
        })
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to update payment status');
    }

    return {
      success: true,
      data: data
    };
  } catch (error) {
    console.error('Error updating payment status:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Add payment reference to a subscription
 * @param {string} applicationNumber - The application number
 * @param {string} paymentReference - The transaction ID
 * @returns {Promise<Object>} Response data
 */
export const addPaymentReference = async (applicationNumber, paymentReference) => {
  try {
    const response = await fetch(
      `${BASE}/payment-reference`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          application_number: applicationNumber, 
          payment_reference: paymentReference
        })
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to add payment reference');
    }

    return {
      success: true,
      data: data
    };
  } catch (error) {
    console.error('Error adding payment reference:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Save complete payment data (status + reference)
 * @param {Object} paymentData - Payment data object
 * @param {string} paymentData.applicationNumber - The application number
 * @param {string} paymentData.transactionId - The payment transaction ID
 * @param {string} paymentData.status - Payment status ('success' or 'failed')
 * @returns {Promise<Object>} Combined response
 */
export const savePaymentData = async ({ applicationNumber, transactionId, status }) => {
  try {
    // Normalize status to match backend requirements (lowercase)
    const normalizedStatus = status === 'success' ? 'success' : 'failed';

    // 1. Update payment status
    const statusResult = await updatePaymentStatus(applicationNumber, normalizedStatus);
    
    if (!statusResult.success) {
      throw new Error(`Status update failed: ${statusResult.error}`);
    }

    // 2. Add payment reference
    const referenceResult = await addPaymentReference(applicationNumber, transactionId);
    
    if (!referenceResult.success) {
      throw new Error(`Reference update failed: ${referenceResult.error}`);
    }

    return {
      success: true,
      message: 'Payment data saved successfully',
      data: {
        statusUpdate: statusResult.data,
        referenceUpdate: referenceResult.data
      }
    };
  } catch (error) {
    console.error('Error saving payment data:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

export default {
  updatePaymentStatus,
  addPaymentReference,
  savePaymentData
};